import { Controller } from "@hotwired/stimulus"

// Client-side sort for document-card grids. Reorders the cards within one or
// more grids by a chosen key, updates the trigger label, ticks the active
// option, persists the choice in localStorage, and toggles the menu. Cards
// expose data-sort-date (epoch seconds) and data-sort-title (lowercased).
// The "Create new …" tile has neither attribute, so it stays pinned first.
const LABELS = { recent: "Recent", oldest: "Oldest", title: "Name (A–Z)" }

export default class extends Controller {
  static targets = ["trigger", "menu", "label", "grid", "option"]
  static values  = { storageKey: String, default: { type: String, default: "recent" } }

  connect() {
    this.boundOutside = this.closeOutside.bind(this)
    document.addEventListener("click", this.boundOutside)
    let saved = ""
    try { saved = localStorage.getItem(this.storageKeyValue) || "" } catch (_) {}
    this.apply(LABELS[saved] ? saved : this.defaultValue)
  }

  disconnect() { document.removeEventListener("click", this.boundOutside) }

  toggle(event) {
    event.stopPropagation()
    if (this.hasMenuTarget) this.setExpanded(this.menuTarget.classList.toggle("hidden") === false)
  }

  select(event) {
    const key = event.currentTarget.dataset.sort
    if (!LABELS[key]) return
    this.apply(key)
    try { localStorage.setItem(this.storageKeyValue, key) } catch (_) {}
    this.closeMenu()
  }

  apply(key) {
    if (this.hasLabelTarget) this.labelTarget.textContent = LABELS[key]
    this.gridTargets.forEach((grid) => {
      const cards = Array.from(grid.querySelectorAll(":scope > [data-sort-date]"))
      cards.sort((a, b) => {
        if (key === "title") {
          return (a.dataset.sortTitle || "").localeCompare(b.dataset.sortTitle || "")
        }
        const da = Number(a.dataset.sortDate || 0), db = Number(b.dataset.sortDate || 0)
        return key === "oldest" ? da - db : db - da
      })
      grid.append(...cards)
    })
    this.optionTargets.forEach((opt) => {
      const active = opt.dataset.sort === key
      opt.setAttribute("aria-checked", String(active))
      const check = opt.querySelector("[data-check]")
      if (check) check.classList.toggle("opacity-0", !active)
    })
  }

  closeOutside(event) {
    if (this.hasMenuTarget && !this.element.contains(event.target)) this.closeMenu()
  }

  closeMenu() {
    if (this.hasMenuTarget) this.menuTarget.classList.add("hidden")
    this.setExpanded(false)
  }

  setExpanded(open) {
    if (this.hasTriggerTarget) this.triggerTarget.setAttribute("aria-expanded", String(open))
  }
}
